import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../auth/AuthContext';
import { API_BASE, s, authHeaders, fmtTime } from './sharedStyles';

const LIST_POLL = 5000;
const DETAIL_POLL = 2000;

export default function QueueInspectorPage() {
  const { token } = useAuth();
  const [queues, setQueues] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [graceHolds, setGraceHolds] = useState([]);
  const [showGrace, setShowGrace] = useState(false);
  const [error, setError] = useState(null);

  const fetchQueues = useCallback(() => {
    fetch(`${API_BASE}/api/v1/monitor/queues`, { headers: authHeaders(token) })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then((data) => { setQueues(data.queues || []); setError(null); })
      .catch(() => setError('Failed to load queues'));
  }, [token]);

  const fetchSnapshot = useCallback((queueId) => {
    fetch(`${API_BASE}/api/v1/monitor/queues/${encodeURIComponent(queueId)}`, { headers: authHeaders(token) })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then(setSnapshot)
      .catch(() => setError('Failed to load queue snapshot'));
  }, [token]);

  const fetchGrace = useCallback(() => {
    fetch(`${API_BASE}/api/v1/monitor/grace-holds`, { headers: authHeaders(token) })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then((data) => setGraceHolds(data.holds || []))
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    fetchQueues();
    const id = setInterval(fetchQueues, LIST_POLL);
    return () => clearInterval(id);
  }, [fetchQueues]);

  useEffect(() => {
    if (!selectedId) return;
    fetchSnapshot(selectedId);
    const id = setInterval(() => fetchSnapshot(selectedId), DETAIL_POLL);
    return () => clearInterval(id);
  }, [selectedId, fetchSnapshot]);

  useEffect(() => {
    if (!showGrace) return;
    fetchGrace();
    const id = setInterval(fetchGrace, LIST_POLL);
    return () => clearInterval(id);
  }, [showGrace, fetchGrace]);

  const copy = (text) => navigator.clipboard?.writeText(text);

  return (
    <div>
      <main style={{ ...s.main, maxWidth: 1400 }}>
        <h1 style={s.pageTitle}>Queue Inspector</h1>
        <div style={s.detail}>Read-only view of live Redis queue state. All snapshot views are audited.</div>

        {error && <div style={{ ...s.card, borderColor: '#EF4444', color: '#EF4444', marginTop: '0.75rem' }}>{error}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: '1rem', marginTop: '1rem' }}>
          <div style={s.card}>
            <div style={s.cardTitle}>Active Queues ({queues.length})</div>
            {queues.length === 0 && <div style={s.detail}>No active queues.</div>}
            {queues.map((q) => (
              <button
                key={q.queueId}
                onClick={() => setSelectedId(q.queueId)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                  padding: '0.6rem 0.75rem', marginBottom: 6, borderRadius: 8,
                  border: '1px solid ' + (selectedId === q.queueId ? '#7C3AED' : '#334155'),
                  background: selectedId === q.queueId ? 'rgba(124,58,237,0.15)' : '#0F172A',
                  color: '#F8FAFC', fontSize: '0.85rem',
                }}
              >
                <div style={{ fontWeight: 700 }}>{q.shopName || q.queueId}</div>
                <div style={{ ...s.detail, marginTop: 2 }}>{q.tenantName || '—'}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                  <span style={s.detail}>Size: <strong style={{ color: '#F8FAFC' }}>{q.size}</strong></span>
                  <span style={s.detail}>#{q.headTicketNumber ?? '—'}</span>
                </div>
              </button>
            ))}

            <button
              onClick={() => setShowGrace(!showGrace)}
              style={{ ...s.outlineBtn, width: '100%', marginTop: '0.75rem' }}
            >
              {showGrace ? 'Hide' : 'Show'} Grace Holds
            </button>
          </div>

          <div>
            {!selectedId && !showGrace && (
              <div style={s.card}><div style={s.detail}>Select a queue to view its snapshot.</div></div>
            )}

            {selectedId && snapshot && (
              <div style={s.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#F8FAFC' }}>{snapshot.shopName || snapshot.queueId}</div>
                    <div style={s.detail}>{snapshot.tenantName || ''}</div>
                  </div>
                  <div>
                    <span style={{
                      ...s.statusBadge(snapshot.acceptingNewCustomers === true),
                      marginRight: 8,
                    }}>{snapshot.acceptingNewCustomers === false ? 'Closed' : 'Accepting'}</span>
                    <span style={{ fontSize: '0.8rem', color: '#94A3B8' }}>Queue ID:</span>
                    <code style={{ marginLeft: 4, fontSize: '0.8rem' }}>{snapshot.queueId}</code>
                  </div>
                </div>

                {snapshot.called && (
                  <div style={{ ...s.card, marginTop: '1rem', borderColor: '#7C3AED', marginBottom: 0 }}>
                    <div style={s.cardTitle}>Currently Called</div>
                    <div style={{ display: 'flex', gap: '2rem' }}>
                      <div><div style={s.metricBig}>#{snapshot.called.ticketNumber ?? '—'}</div><div style={s.metricLabel}>Ticket</div></div>
                      <div>
                        <div style={{ fontSize: '1.1rem', color: '#F8FAFC', fontWeight: 700 }}>{snapshot.called.deskLabel || '—'}</div>
                        <div style={s.metricLabel}>Desk</div>
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ marginTop: '1rem' }}>
                  <div style={s.cardTitle}>Queue ({snapshot.size})</div>
                  {snapshot.entries.length === 0 ? (
                    <div style={s.detail}>Empty.</div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ color: '#94A3B8', textAlign: 'left' }}>
                          <th style={th}>Pos</th>
                          <th style={th}>Ticket #</th>
                          <th style={th}>Joined</th>
                          <th style={th}>Source</th>
                          <th style={th}>Name</th>
                          <th style={th}>UUID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {snapshot.entries.map((e) => (
                          <tr key={e.ticketUuid}>
                            <td style={td}>{e.position}</td>
                            <td style={{ ...td, color: '#F8FAFC', fontWeight: 700 }}>{e.ticketNumber ?? '—'}</td>
                            <td style={td}>{fmtTime(e.joinedAt)}</td>
                            <td style={td}>{e.source}</td>
                            <td style={{ ...td, fontStyle: 'italic', color: '#94A3B8' }}>{e.firstNameMasked || '—'}</td>
                            <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              <button
                                onClick={() => copy(e.ticketUuid)}
                                style={{ ...s.outlineBtn, padding: '2px 8px', fontSize: '0.7rem' }}
                              >
                                {e.ticketUuid.slice(0, 8)}…
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}

            {showGrace && (
              <div style={{ ...s.card, marginTop: selectedId ? '1rem' : 0 }}>
                <div style={s.cardTitle}>Grace Holds ({graceHolds.length})</div>
                {graceHolds.length === 0 ? (
                  <div style={s.detail}>None.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ color: '#94A3B8', textAlign: 'left' }}>
                        <th style={th}>Queue</th>
                        <th style={th}>Ticket UUID</th>
                        <th style={th}>Expires</th>
                      </tr>
                    </thead>
                    <tbody>
                      {graceHolds.map((g, i) => (
                        <tr key={i}>
                          <td style={td}>{g.queueId}</td>
                          <td style={{ ...td, fontFamily: 'monospace' }}>{g.ticketUuid.slice(0, 8)}…</td>
                          <td style={td}>{fmtTime(g.expiresAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

const th = { padding: '8px 10px', borderBottom: '1px solid #334155', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' };
const td = { padding: '8px 10px', borderBottom: '1px solid #1E293B', color: '#CBD5E1' };
